/**
 * Kolejka intencji (PLAN-V2 §3.3): tap poza zasięgiem → pies podchodzi (moveTo), potem akcja.
 * Logika w ObjectLayer / tu — NIE w scenach.
 */

import type { InteractionKind } from './types.js';

export type Intention = {
  /** Unikalny id intencji (kolejność / anulowanie). */
  id: string;
  /** Id instancji InteractiveObject. */
  objectId: string;
  defId: string;
  kind: InteractionKind;
  /** Cel podejścia (pozycja obiektu). */
  approachX: number;
  approachY: number;
  /** Zasięg, w którym akcja może wystartować. */
  range: number;
};

export type IntentionQueueState = {
  current: Intention | null;
  pending: Intention[];
  /** Monotoniczny generator id. */
  nextId: number;
};

export function createIntentionQueue(): IntentionQueueState {
  return { current: null, pending: [], nextId: 1 };
}

export type EnqueueResult = {
  queue: IntentionQueueState;
  intention: Intention;
  /** true gdy od razu jest current (kolejka była pusta). */
  started: boolean;
};

/**
 * Dodaje intencję. Jeśli brak current — staje się current.
 * Domyślnie nowa intencja tego samego objectId zastępuje pending dla tego obiektu
 * i anuluje current, jeśli dotyczy tego samego obiektu (dziecko zmienia zdanie).
 */
export function enqueueIntention(
  queue: IntentionQueueState,
  input: Omit<Intention, 'id'>,
  opts?: { replaceSameObject?: boolean },
): EnqueueResult {
  const replaceSame = opts?.replaceSameObject !== false;
  let pending = queue.pending;
  let current = queue.current;
  const nextId = queue.nextId;

  if (replaceSame) {
    pending = pending.filter((p) => p.objectId !== input.objectId);
    if (current && current.objectId === input.objectId) {
      current = null;
    }
  }

  const intention: Intention = {
    ...input,
    id: `int-${nextId}`,
  };

  if (!current) {
    return {
      queue: { current: intention, pending, nextId: nextId + 1 },
      intention,
      started: true,
    };
  }

  return {
    queue: {
      current,
      pending: [...pending, intention],
      nextId: nextId + 1,
    },
    intention,
    started: false,
  };
}

/** Odległość 2D. */
export function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return Math.hypot(dx, dy);
}

/** Czy cel jest w zasięgu intencji. */
export function isInRange(
  intention: Intention,
  targetX: number,
  targetY: number,
): boolean {
  return dist2(targetX, targetY, intention.approachX, intention.approachY) <= intention.range;
}

/**
 * Po zakończeniu current (akcja wykonana) — awansuj pending.
 */
export function completeCurrent(queue: IntentionQueueState): {
  queue: IntentionQueueState;
  completed: Intention | null;
  next: Intention | null;
} {
  const completed = queue.current;
  if (!completed) {
    return { queue, completed: null, next: null };
  }
  const [next, ...rest] = queue.pending;
  return {
    queue: {
      current: next ?? null,
      pending: rest,
      nextId: queue.nextId,
    },
    completed,
    next: next ?? null,
  };
}

/** Anuluj całą kolejkę (np. wyjście ze sceny). */
export function clearQueue(queue: IntentionQueueState): IntentionQueueState {
  return { current: null, pending: [], nextId: queue.nextId };
}

/**
 * Anuluj current i pending dla objectId; jeśli current zniknął — awansuj pending.
 */
export function cancelForObject(
  queue: IntentionQueueState,
  objectId: string,
): IntentionQueueState {
  const pending = queue.pending.filter((p) => p.objectId !== objectId);
  const cancelledCurrent =
    queue.current !== null && queue.current.objectId === objectId;

  if (cancelledCurrent) {
    const [next, ...rest] = pending;
    return {
      current: next ?? null,
      pending: rest,
      nextId: queue.nextId,
    };
  }

  return {
    current: queue.current,
    pending,
    nextId: queue.nextId,
  };
}
